import { HttpClient, HttpContext, HttpHeaders, HttpParams } from "@angular/common/http";
import { Signal } from "@angular/core";
import { Observable } from "rxjs";

type HttpRequestOptions = {
  headers?: HttpHeaders | {
      [header: string]: string | string[];
  };
  observe: "events";
  context?: HttpContext;
  params?: HttpParams | {
      [param: string]: string | number | boolean | ReadonlyArray<string | number | boolean>;
  };
  reportProgress?: boolean;
  responseType?: "json";
  withCredentials?: boolean;
  transferCache?: {
      includeHeaders?: string[];
  } | boolean;
};

type HttpDeleteOptions = {
  headers?: HttpHeaders | {
      [header: string]: string | string[];
  };
  observe: "events";
  context?: HttpContext;
  params?: HttpParams | {
      [param: string]: string | number | boolean | (string | number | boolean)[];
  };
  reportProgress?: boolean;
  responseType?: "json";
  withCredentials?: boolean;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  body?: any | null;
};

/**
 * Authorizes the given http client using a decorator.
 * The returned object should be usable exactly like a http client, but all requests are authorized with the given access token.
 * When the access token signal is updated, all subsequent requests will use the new access token.
 * @param http
 * @param accessTokenReadOnly 
 * @returns decorated http client
 */
export function authorizeHttp(http: HttpClient, accessTokenReadOnly: Signal<string|null>) {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  function authorizeHttpOptions(options: any) {
    const accessToken = accessTokenReadOnly();
    if (accessToken === null) { return options; }
    // eslint-disable-next-line no-param-reassign
    if (!options) { options = {}; }
    if (!options.headers) { options.headers = {}; }
    if (options.headers instanceof HttpHeaders) {
      options.headers.append('Authorization', `Bearer ${accessToken}`);
    } else {
      options.headers['Authorization'] = `Bearer ${accessToken}`;
    }
    if (!options['responseType']) { options['responseType'] = 'json'; }
    return options;
  };

  return {
    get<T>(url: string, options?: HttpRequestOptions) { return http.get<T>(url, authorizeHttpOptions(options) as HttpRequestOptions) as Observable<T>; },
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    post<T>(url: string, body: any | null, options?: HttpRequestOptions) { return http.post<T>(url, body, authorizeHttpOptions(options) as HttpRequestOptions) as Observable<T>; },
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    patch<T>(url: string, body: any | null, options?: HttpRequestOptions) { return http.patch<T>(url, body, authorizeHttpOptions(options) as HttpRequestOptions) as Observable<T>; },
    delete<T>(url: string, body?: any | null, options?: HttpDeleteOptions) {
      const authorizedOptions = authorizeHttpOptions(options) as HttpDeleteOptions;
      if (body !== undefined) {
        authorizedOptions.body = body;
      }
      return http.delete<T>(url, authorizedOptions) as Observable<T>;
    },
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    put<T>(url: string, body: any | null, options?: HttpRequestOptions) { return http.put<T>(url, body, authorizeHttpOptions(options) as HttpRequestOptions) as Observable<T>; },
    request: http.request,
    handler: http['handler'],
    head: http.head,
    jsonp: http.jsonp,
    options: http.options,
  };
}
